// src/pages/viewtemplate.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
  pointerWithin,
  DragOverlay, // Added DragOverlay
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import infoCircle from "../assets/info-circle.svg";

import PageHeader from "../components/PageHeader";
import Help from "../components/Help";
import { helpPagesData } from "../utils/helper";
import trash from "../assets/red-trash.svg";
import textIcon from "../assets/text.svg";
import paragraphIcon from "../assets/paragraph.svg";
import buttonIcon from "../assets/button.svg";
import uploadImageIcon from "../assets/uploadImage.svg";
import tableIcon from "../assets/table.svg";
import urlIcon from "../assets/url.svg";
import subtextIcon from "../assets/subtext.svg";
import videoIcon from "../assets/video.svg";
import emptysection from "../assets/empty-section.svg";
import sortIcon from "../assets/draghandle.svg";
import undoIcon from "../assets/undo.svg";
import dropdownIcon from "../assets/dropdown-icon.svg";
import SwitchToggle from "../components/switch";
import { useToast } from "../components/ToastProvider";

// Actions / API helpers (make sure these are exported from your action file)
import { StandardFieldList, FetchTemplate, UpdateTemplate, DeleteTemplate } from "../action/sectiondata";
import { Spinner } from "react-bootstrap";

/* ---------------------------
   Utilities
----------------------------*/
const uid = () => Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
const tplMapKey = (id) => `tpl-map-${id}`;
const NESTED_DROP_PREFIX = "nested-drop-";

const isContainerField = (field) =>
  field?.type === "section" || field?.type === "list";

const getNestedDropId = (fieldId) => `${NESTED_DROP_PREFIX}${fieldId}`;

const insertAtIndex = (items, field, index) => {
  const copy = [...items];
  const safeIndex = index === -1 || index === null || index === undefined ? copy.length : index;

  if (safeIndex >= copy.length) copy.push(field);
  else copy.splice(safeIndex, 0, field);

  return copy;
};

const updateFieldTree = (fields, targetId, updater) =>
  fields.map((field) => {
    if (field.id === targetId) {
      return updater(field);
    }

    if (Array.isArray(field.nestedFields) && field.nestedFields.length > 0) {
      return {
        ...field,
        nestedFields: updateFieldTree(field.nestedFields, targetId, updater),
      };
    }

    return field;
  });

const deleteFieldFromTree = (fields, targetId) =>
  fields.reduce((acc, field) => {
    if (field.id === targetId) return acc;

    const nextField =
      Array.isArray(field.nestedFields) && field.nestedFields.length > 0
        ? {
          ...field,
          nestedFields: deleteFieldFromTree(field.nestedFields, targetId),
        }
        : field;

    acc.push(nextField);
    return acc;
  }, []);

const findFieldById = (fields, id) => {
  for (const field of fields) {
    if (field.id === id) return field;

    if (Array.isArray(field.nestedFields) && field.nestedFields.length > 0) {
      const nestedMatch = findFieldById(field.nestedFields, id);
      if (nestedMatch) return nestedMatch;
    }
  }

  return null;
};

const findNestedDropTarget = (fields, overId) => {
  for (const field of fields) {
    if (!isContainerField(field)) continue;

    if (getNestedDropId(field.id) === overId) {
      return { parentId: field.id, items: field.nestedFields || [], isContainerItself: true };
    }

    const nestedFields = field.nestedFields || [];
    const directChildIndex = nestedFields.findIndex((child) => child.id === overId);
    if (directChildIndex !== -1) {
      return { parentId: field.id, items: nestedFields, isContainerItself: false };
    }

    const nestedMatch = findNestedDropTarget(nestedFields, overId);
    if (nestedMatch) return nestedMatch;
  }

  return null;
};

const insertIntoNestedField = (fields, parentId, newField, index) =>
  fields.map((field) => {
    if (field.id === parentId) {
      return {
        ...field,
        nestedFields: insertAtIndex(field.nestedFields || [], newField, index),
      };
    }

    if (Array.isArray(field.nestedFields) && field.nestedFields.length > 0) {
      return {
        ...field,
        nestedFields: insertIntoNestedField(field.nestedFields, parentId, newField, index),
      };
    }

    return field;
  });

const findNestedParentByChildId = (fields, childId) => {
  for (const field of fields) {
    if (Array.isArray(field.nestedFields) && field.nestedFields.some((child) => child.id === childId)) {
      return { parentId: field.id, items: field.nestedFields };
    }

    if (Array.isArray(field.nestedFields) && field.nestedFields.length > 0) {
      const nestedMatch = findNestedParentByChildId(field.nestedFields, childId);
      if (nestedMatch) return nestedMatch;
    }
  }

  return null;
};

const reorderNestedFields = (fields, parentId, oldIndex, newIndex) =>
  fields.map((field) => {
    if (field.id === parentId) {
      return {
        ...field,
        nestedFields: arrayMove(field.nestedFields || [], oldIndex, newIndex),
      };
    }

    if (Array.isArray(field.nestedFields) && field.nestedFields.length > 0) {
      return {
        ...field,
        nestedFields: reorderNestedFields(field.nestedFields, parentId, oldIndex, newIndex),
      };
    }

    return field;
  });

const validateFieldTree = (fields, markInvalid) =>
  fields.map((field) => {
    const errs = {};

    if (!field.labelText || !field.labelText.trim()) {
      errs.labelText = "Field label is required";
      markInvalid();
    }

    return {
      ...field,
      nestedFields: Array.isArray(field.nestedFields)
        ? validateFieldTree(field.nestedFields, markInvalid)
        : [],
      __errors: errs,
    };
  });

const collectMissingLabelIds = (fields, bucket = new Set()) => {
  fields.forEach((field) => {
    if (!field.labelText || !field.labelText.trim()) bucket.add(field.id);

    if (Array.isArray(field.nestedFields) && field.nestedFields.length > 0) {
      collectMissingLabelIds(field.nestedFields, bucket);
    }
  });

  return bucket;
};

const serializeField = (field) => {
  const payload = {
    label: (field.labelText || field.label).trim(),
    type: field.type,
    required: !!field.required,
    placeholder: field.placeholderEnabled ? field.placeholder || "" : "",
    standard_field:
      typeof field.standard_field === "string"
        ? field.standard_field
        : field.standard_field?._id || null,
  };

  if (isContainerField(field)) {
    payload.fields = (field.nestedFields || []).map(serializeField);
  }

  return payload;
};



/* ---------------------------
   Icon fallback map
----------------------------*/
const ICON_MAP = {
  text: textIcon,
  textarea: paragraphIcon,
  button: buttonIcon,
  image: uploadImageIcon,
  table: tableIcon,
  url: urlIcon,
  subtext: subtextIcon,
  video: videoIcon,
};

/* ---------------------------
   Small UI components
----------------------------*/
const EmptyBox = ({ title, desc }) => (
  <div className="empt-state">
    <img src={emptysection} alt="empty" />
    <h6 className="text-center">{title}</h6>
    <p className="text-muted text-center">{desc}</p>
  </div>
);

const DropPlaceholder = () => <div className="dnd-hover-placeholder" />;

/* ---------------------------
   Draggable library item
----------------------------*/
function DraggableField({ id, label, icon, payloadType, placeholderEnabled }) {
  const { listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: { id, label, icon, type: payloadType, source: "library", placeholderEnabled },
  });

  const style = {
    // transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined, // Removed transform for overlay usage
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? "grabbing" : "grab",
    // boxShadow: isDragging
    //   ? "-19px 10px 13px 0px #0000000D, -34px 18px 15px 0px #00000003, -53px 28px 17px 0px #00000000"
    //   : undefined,
    // background: isDragging ? "#fff" : undefined,
    // zIndex: isDragging ? 999 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} className="field-item" onDoubleClick={(e) => e.preventDefault()}>
      <span className="field-label">{label}</span>
      {icon && <img src={icon} alt={label} className="draggable-icon" />}
    </div>
  );
}

/* ---------------------------
   Reusable Field Item UI (for Sortable & Overlay)
----------------------------*/
const FieldItem = React.forwardRef(({
  item,
  onLabelChange,
  onDelete,
  isExpanded,
  toggleExpand,
  onToggleChange,
  isMulti,
  dragHandleProps, // { attributes, listeners, ref }
  style,
  isOverlay, // true if rendering in DragOverlay
  isDragging, // true if this is the source item being dragged
  expandedSet,
}, ref) => {

  // Recursively find standard field info
  const findStandardFieldRecursive = (node) => {
    if (!node || typeof node !== "object") return null;
    if (node.standard_field) return node.standard_field;
    if (Array.isArray(node.nestedFields)) {
      for (const child of node.nestedFields) {
        const found = findStandardFieldRecursive(child);
        if (found) return found;
      }
    }
    if (Array.isArray(node.fields)) {
      for (const child of node.fields) {
        const found = findStandardFieldRecursive(child);
        if (found) return found;
      }
    }
    return null;
  };

  const std = findStandardFieldRecursive(item);
  const iconUrl = std?.icon
    ? `${process.env.REACT_APP_BUCKET_URL}${std.icon}`
    : item.icon;

  const className = `field-item dropped ${isOverlay ? "field-overlay" : ""}`;
  const wrapperStyle = {
    ...style,
    opacity: isDragging ? 0.3 : 1, // Fade original item when dragging
  };
  const showNestedDropZone = isContainerField(item);

  return (
    <div ref={ref} style={wrapperStyle} className="field-name-wrapper">
      <img
        ref={dragHandleProps?.ref}
        {...dragHandleProps?.attributes}
        {...dragHandleProps?.listeners}
        className="field-sort-btn"
        src={sortIcon}
        alt="move"
        draggable={false}
        style={{ cursor: isOverlay ? 'grabbing' : 'grab' }}
      />
      <div className={className}>
        <div className="dropped-header">
          <div className="dropped-left">
            {iconUrl && <img src={iconUrl} className="dropped-icon" alt="icon" />}
            <span className="dropped-label">
              {std?.title || item.label}
            </span>
          </div>

          <div className="dropped-right">
            {!isOverlay && (
              <>
                <button className="delete-field-btn" onClick={() => onDelete && onDelete(item.id)} type="button">
                  Delete
                </button>

                <button className="expand-btn" onClick={() => toggleExpand && toggleExpand(item.id)} type="button">
                  <img src={dropdownIcon} alt="toggle" className={`dropdown-arrow ${isExpanded ? "open" : ""}`} />
                </button>
              </>
            )}
          </div>
        </div>

        {isExpanded && !isOverlay && (
          <div className="dropped-body">
            <div className="dropped-fields">
              <input
                type="text"
                placeholder="Enter Field label or title"
                value={item.labelText || ""}
                onChange={(e) => onLabelChange && onLabelChange(item.id, e.target.value, "labelText")}
              />

              {item.placeholderEnabled && item.placeholder !== null && (
                <input
                  type="text"
                  placeholder="Enter Placeholder"
                  value={item.placeholder || ""}
                  onChange={(e) => onLabelChange && onLabelChange(item.id, e.target.value, "placeholder")}
                />
              )}
              {isMulti && (
                <div className="switch-btn">
                  Enable this if the input will appear multiple times.
                  <SwitchToggle checked={!!item.toggle} onChange={(e) => onToggleChange && onToggleChange(item.id, e.target.checked)} />
                </div>
              )}

              {showNestedDropZone && (
                <NestedFieldDropArea
                  parentItem={item}
                  fields={item.nestedFields || []}
                  onLabelChange={onLabelChange}
                  onDelete={onDelete}
                  expandedSet={expandedSet}
                  toggleExpand={toggleExpand}
                  onToggleChange={onToggleChange}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

function NestedFieldDropArea({
  parentItem,
  fields,
  onLabelChange,
  onDelete,
  expandedSet,
  toggleExpand,
  onToggleChange,
}) {
  const { setNodeRef, isOver } = useDroppable({ id: getNestedDropId(parentItem.id) });

  return (
    <div ref={setNodeRef} className={`nested-drop-zone ${isOver ? "over" : ""}`}>
      <div className="nested-drop-zone__title">
        {parentItem.type === "section" ? "Section Fields" : "List Fields"}
      </div>

      {fields.length === 0 ? (
        <div className="nested-drop-zone__empty">Drag fields here</div>
      ) : (
        <SortableContext
          items={fields.map((field) => field.id)}
          strategy={verticalListSortingStrategy}
        >
          {fields.map((field) => (
            <SortableField
              key={field.id}
              item={field}
              onLabelChange={onLabelChange}
              onDelete={onDelete}
              isExpanded={expandedSet.has(field.id)}
              toggleExpand={toggleExpand}
              onToggleChange={onToggleChange}
              expandedSet={expandedSet}
            />
          ))}
        </SortableContext>
      )}
    </div>
  );
}

/* ---------------------------
   Sortable field component (dropped)
----------------------------*/
function SortableField({ item, onLabelChange, onDelete, isExpanded, toggleExpand, onToggleChange, isMulti, expandedSet }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({
      id: item.id,
      data: { item, type: "sortable" }, // Pass item data for overlay detection
      transition: {
        duration: 250,
        easing: "cubic-bezier(0.25, 1, 0.5, 1)",
      }

    });


  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };


  return (
    <FieldItem
      ref={setNodeRef}
      style={style}
      item={item}
      onLabelChange={onLabelChange}
      onDelete={onDelete}
      isExpanded={isExpanded}
      toggleExpand={toggleExpand}
      onToggleChange={onToggleChange}
      isMulti={isMulti}
      expandedSet={expandedSet}
      dragHandleProps={{
        ref: setActivatorNodeRef,
        attributes,
        listeners
      }}
      isDragging={isDragging}
    />
  );
}

/* -----------------------------------------

  Droppable Fixed container (inside DndContext)

----------------------------------------- */

const DroppableFixed = ({
  fields,
  onLabelChange,
  onDelete,
  expandedSet,
  toggleExpand,
  onToggleChange,
  previewIndex,
  showPreview,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: "fixed-area" });

  return (
    <div ref={setNodeRef} className={`drop-box ${isOver ? "over" : ""}`}>
      <h6>Fixed Fields</h6>
      {/* {fields.length !== 0 && showPreview && previewIndex === 0 && <DropPlaceholder />} */}

      {fields.length === 0 ? (
        <EmptyBox
          title="Drop Fixed Fields Here"
          desc="Add single-instance fields like title or subtitle that appear once per section."
        />
      ) : (
        <SortableContext
          items={fields.map((f) => f.id)}
          strategy={verticalListSortingStrategy}
        >
          {fields.map((it, index) => (
            <React.Fragment key={it.id}>
              {showPreview && previewIndex === index && <DropPlaceholder />}
              <SortableField
                item={it}
                onLabelChange={onLabelChange}
                onDelete={onDelete}
                isExpanded={expandedSet.has(it.id)}
                toggleExpand={toggleExpand}
                onToggleChange={onToggleChange}
                expandedSet={expandedSet}
              />
            </React.Fragment>
          ))}
          {showPreview && previewIndex === fields.length && <DropPlaceholder />}
        </SortableContext>
      )}
    </div>
  );
};



/* -----------------------------------------

  Droppable Multi container (inside DndContext)

----------------------------------------- */

const DroppableMulti = ({
  fields,
  onLabelChange,
  onDelete,
  expandedSet,
  toggleExpand,
  onToggleChange,
  previewIndex,
  showPreview,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: "multi-area" });

  return (
    <div ref={setNodeRef} className={`drop-box ${isOver ? "over" : ""}`}>
      <h6>Fields for Multiple Instance</h6>
      {/* {showPreview && previewIndex === 0 && <DropPlaceholder />} */}

      {fields.length === 0 ? (
        <EmptyBox
          title="Drop Fields for Multiple Entries"
          desc="Add input fields that generate repeatable sets such as testimonials or FAQs."
        />
      ) : (
        <SortableContext
          items={fields.map((f) => f.id)}
          strategy={verticalListSortingStrategy}
        >
          {fields.map((it, index) => (
            <React.Fragment key={it.id}>
              {showPreview && previewIndex === index && <DropPlaceholder />}
              <SortableField
                item={it}
                onLabelChange={onLabelChange}
                onDelete={onDelete}
                isExpanded={expandedSet.has(it.id)}
                toggleExpand={toggleExpand}
                onToggleChange={onToggleChange}
                isMulti={true}
                expandedSet={expandedSet}
              />
            </React.Fragment>
          ))}
          {showPreview && previewIndex === fields.length && <DropPlaceholder />}
        </SortableContext>
      )}
    </div>
  );
};


/* ---------------------------
   Convert API fields into UI items (recursive)
----------------------------*/
function convertAPIToUI(fields = [], area = "fixed", library = []) {
  return fields.map((child) => {
    const isRepeater = child.type === "repeater";
    const isContainer = isContainerField(child);

    // Prefer exact library match for icons/labels
    const match = bestLibraryMatch(child.label, child.type, library);

    const uiField = {
      id: `${area}-${uid()}`,
      type: child.type,
      originalType: isRepeater ? (child.fields?.[0]?.type || "text") : child.type,
      label: child.label,
      standard_field: child.standard_field?._id ? { _id: child.standard_field._id } : null,
      icon: child.standard_field?.icon
        ? `${process.env.REACT_APP_BUCKET_URL}${child.standard_field.icon}`
        : match?.icon || ICON_MAP[child.type] || null,
      labelText: child.label,
      displayLabel: match ? match.label : child.label,
      required: !!child.required,
      placeholderEnabled: !!(child.placeholder !== undefined && child.placeholder !== null && child.placeholder !== ""),
      placeholder: child.placeholder || "",
      toggle: isRepeater,
      nested: isRepeater || isContainer,
      nestedFields: [],
      sourceLibId: match ? match.id : null,
      __errors: {},
    };

    if (isRepeater) {
      // old-style repeater: it has one field inside 'fields'
      uiField.nestedFields = convertAPIToUI(child.fields || [], area, library);
    } else if (isContainer) {
      // new-style section/list: it has multiple fields inside 'fields'
      uiField.nestedFields = convertAPIToUI(child.fields || [], area, library);
    }

    return uiField;
  });
}

/* ---------------------------
   Helper to find library match
   - Prefer sourceLibId mapping (stored in localStorage)
   - Then prefer exact label match
   - Then prefer same type fallback
----------------------------*/
function bestLibraryMatch(savedLabel = "", savedType = "", library = []) {
  if (!library || library.length === 0) return null;
  const label = (savedLabel || "").toString().trim().toLowerCase();

  const exact = library.find((l) => l.label.trim().toLowerCase() === label);
  if (exact) return exact;

  const contains = library.find((l) => {
    const libLabel = (l.label || "").toLowerCase();
    return libLabel && (label.includes(libLabel) || libLabel.includes(label));
  });
  if (contains) return contains;

  const sameType = library.filter((l) => l.type === savedType);
  if (sameType.length === 1) return sameType[0];
  return sameType[0] || library[0];
}

/* ---------------------------
   Main Component
----------------------------*/
export default function ViewTemplate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [templateName, setTemplateName] = useState("");
  const [fixedFields, setFixedFields] = useState([]);
  const [multiFields, setMultiFields] = useState([]);
  const [backup, setBackup] = useState({ fixed: [], multi: [] });
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [library, setLibrary] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [libError, setLibError] = useState(null);

  const [expandedSet, setExpandedSet] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [errors, setErrors] = useState({ templateName: null });

  // Drag overlay state
  const [activeId, setActiveId] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [dragOverInfo, setDragOverInfo] = useState({ containerId: null, index: null });

  // const { setNodeRef: setFixedNodeRef } = useDroppable({ id: "fixed-area" });
  // const { setNodeRef: setMultiNodeRef } = useDroppable({ id: "multi-area" });
  const isFullLoading = libLoading || loading;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Start drag a bit earlier for smoother feel
      },
    })
  );
  // load library
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLibLoading(true);
      try {
        const res = await StandardFieldList();
        if (!mounted) return;
        if (res?.status === 1) {
          const list = res.data?.list || [];
          const normalized = list.map((it) => ({
            id: it._id,
            label: it.title,
            type: it.type,
            required: it.required ?? false,
            placeholderEnabled: it.placeholder ?? false,
            icon: it.icon ? `${process.env.REACT_APP_BUCKET_URL}${it.icon}` : ICON_MAP[it.type] || null,
          }));
          setLibrary(normalized);
          setLibError(null);
        } else {
          setLibrary([]);
          setLibError(res?.message || "Failed to load fields");
        }
      } catch (err) {
        setLibError("Failed to load fields");
      } finally {
        if (mounted) setLibLoading(false);
      }
    };
    load();
    return () => (mounted = false);
  }, []);
  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const res = await DeleteTemplate(id);
      if (res?.status === 1) {
        showToast("Template deleted successfully", "success");
        navigate("/section");
      } else {
        showToast(res?.message || "Failed to delete template", "error");
      }
    } catch (err) {
      showToast("Failed to delete template", "error");
    } finally {
      setDeleteLoading(false);
      setShowDeletePopup(false);
    }
  };

  // load template after library (so mapping works)
  useEffect(() => {
    let mounted = true;
    const loadTemplate = async () => {
      setLoading(true);
      try {
        const res = await FetchTemplate(id);
        if (!mounted) return;
        if (res?.status === 1 && res.data) {
          const tpl = res.data;
          setTemplateName(tpl.label || "");

          // local mapping stored previously when saving (sourceLibId per index)
          const savedMapRaw = localStorage.getItem(tplMapKey(tpl._id));
          const savedMap = savedMapRaw ? JSON.parse(savedMapRaw) : null;

          // Split fields into fixed and multi (Cards)
          const rawFields = tpl.fields || [];
          const cardsRepeater = rawFields.find(f => f.type === "repeater" && f.label === "Cards");
          const otherFields = rawFields.filter(f => f !== cardsRepeater);

          const fixed = convertAPIToUI(otherFields, "fixed", library);
          const multi = cardsRepeater ? convertAPIToUI(cardsRepeater.fields || [], "multi", library) : [];

          setFixedFields(fixed);
          setMultiFields(multi);
          setBackup({ fixed: [...fixed], multi: [...multi] });

          setApiError("");
        } else {
          setApiError(res?.message || "Failed to load template");
        }
      } catch (err) {
        setApiError("Failed to load template");
      } finally {
        setLoading(false);
      }
    };

    // wait until library load completes so mapping is accurate
    if (!libLoading) loadTemplate();

    return () => (mounted = false);
  }, [id, libLoading, library]);

  /* ---------------------------
     DND handler
  ----------------------------*/
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    setDragOverInfo({ containerId: null, index: null });

    if (active.data.current?.source === "library") {
      setActiveItem(active.data.current);
    } else {
      // Find from state (recursive)
      const found = findFieldById(fixedFields, active.id) ||
        findFieldById(multiFields, active.id);
      setActiveItem(found || active.data.current?.item);
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) {
      setDragOverInfo({ containerId: null, parentId: null, index: null });
      return;
    }

    // Only calculate manual placeholder for Library items. 
    // SortableContext handles reordering placeholders automatically for internal items.
    if (active.data.current?.source !== "library") return;

    let containerId = null;
    let items = [];
    let parentId = null;
    let isContainerItself = false;

    // Determine correct container
    if (over.id === "fixed-area" || fixedFields.some(f => f.id === over.id)) {
      containerId = "fixed-area";
      items = fixedFields;
    } else if (over.id === "multi-area" || multiFields.some(f => f.id === over.id)) {
      containerId = "multi-area";
      items = multiFields;
    } else {
      const nestedMatch =
        findNestedDropTarget(fixedFields, over.id) ||
        findNestedDropTarget(multiFields, over.id);

      if (nestedMatch) {
        containerId = getNestedDropId(nestedMatch.parentId);
        items = nestedMatch.items;
        parentId = nestedMatch.parentId;
        isContainerItself = nestedMatch.isContainerItself;
      }
    }

    if (containerId) {
      // Calculate index
      let index = items.length; // default to end

      if (over.id === containerId || isContainerItself) {
        if (items.length === 0) index = 0;
      } else {
        // We are over an item
        const overIndex = items.findIndex(f => f.id === over.id);
        if (overIndex !== -1) {
          // Calculate if we are below the midpoint of the over item
          let modifier = 0;
          if (active.rect.current?.translated && over.rect) {
            const activeCenterY = active.rect.current.translated.top + active.rect.current.translated.height / 2;
            const overCenterY = over.rect.top + over.rect.height / 2;
            if (activeCenterY > overCenterY) {
              modifier = 1;
            }
          }

          index = overIndex + modifier;
        }
      }
      setDragOverInfo({ containerId, parentId, index });
    } else {
      setDragOverInfo({ containerId: null, parentId: null, index: null });
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setActiveItem(null);
    setDragOverInfo({ containerId: null, parentId: null, index: null });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveItem(null);
    setDragOverInfo({ containerId: null, parentId: null, index: null });

    if (!over) return;

    // dragging from library -> create new item
    if (active.data.current?.source === "library") {
      const source = active.data.current;
      const newField = {
        id: `${source.id}-${uid()}`,
        type: source.type,
        originalType: source.type,
        label: source.label,
        labelText: "",
        displayLabel: source.label,
        icon: source.icon || ICON_MAP[source.type] || null,
        required: source.required ?? false,
        placeholderEnabled: source.placeholderEnabled ?? false,
        placeholder: source.placeholderEnabled ? "" : null,
        toggle: false,
        nested: isContainerField(source),
        nestedFields: [],
        standard_field: { _id: source.id },
        sourceLibId: source.id,
        __errors: {},
      };

      setBackup({ fixed: fixedFields, multi: multiFields });

      // Use the preview index if available for correct placement
      const targetContainer = dragOverInfo.containerId ||
        ((over.id === "fixed-area" || fixedFields.some(f => f.id === over.id)) ? "fixed-area" :
          (over.id === "multi-area" || multiFields.some(f => f.id === over.id)) ? "multi-area" : null);

      if (targetContainer === "fixed-area") {
        const insertIndex = dragOverInfo.containerId === "fixed-area" && dragOverInfo.index !== null
          ? dragOverInfo.index
          : ((over.id === "fixed-area") ? fixedFields.length : fixedFields.findIndex(f => f.id === over.id));

        setFixedFields((prev) => insertAtIndex(prev, newField, insertIndex));
        return;
      }

      if (targetContainer === "multi-area") {
        const insertIndex = dragOverInfo.containerId === "multi-area" && dragOverInfo.index !== null
          ? dragOverInfo.index
          : ((over.id === "multi-area") ? multiFields.length : multiFields.findIndex(f => f.id === over.id));

        setMultiFields((prev) => insertAtIndex(prev, newField, insertIndex));
        return;
      }

      if (dragOverInfo.parentId) {
        const insertIndex = dragOverInfo.index ?? 0;

        if (findNestedDropTarget(fixedFields, over.id)) {
          setFixedFields((prev) =>
            insertIntoNestedField(prev, dragOverInfo.parentId, newField, insertIndex)
          );
        } else if (findNestedDropTarget(multiFields, over.id)) {
          setMultiFields((prev) =>
            insertIntoNestedField(prev, dragOverInfo.parentId, newField, insertIndex)
          );
        }
        return;
      }
      return;
    }

    // reorder within fixed
    if (fixedFields.some((f) => f.id === active.id)) {
      const oldIndex = fixedFields.findIndex((f) => f.id === active.id);
      const newIndex = fixedFields.findIndex((f) => f.id === over.id);
      if (newIndex !== -1 && oldIndex !== newIndex) {
        setBackup({ fixed: fixedFields, multi: multiFields });
        setFixedFields((p) => arrayMove(p, oldIndex, newIndex));
      }
      return;
    }

    // reorder within multi
    if (multiFields.some((f) => f.id === active.id)) {
      const oldIndex = multiFields.findIndex((f) => f.id === active.id);
      const newIndex = multiFields.findIndex((f) => f.id === over.id);
      if (newIndex !== -1 && oldIndex !== newIndex) {
        setBackup({ fixed: fixedFields, multi: multiFields });
        setMultiFields((p) => arrayMove(p, oldIndex, newIndex));
      }
      return;
    }

    const fixedNestedParent = findNestedParentByChildId(fixedFields, active.id);
    const fixedNestedOverParent = findNestedParentByChildId(fixedFields, over.id);
    if (fixedNestedParent && fixedNestedOverParent && fixedNestedParent.parentId === fixedNestedOverParent.parentId) {
      const oldIndex = fixedNestedParent.items.findIndex((field) => field.id === active.id);
      const newIndex = fixedNestedOverParent.items.findIndex((field) => field.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        setBackup({ fixed: fixedFields, multi: multiFields });
        setFixedFields((prev) =>
          reorderNestedFields(prev, fixedNestedParent.parentId, oldIndex, newIndex)
        );
      }
      return;
    }

    const multiNestedParent = findNestedParentByChildId(multiFields, active.id);
    const multiNestedOverParent = findNestedParentByChildId(multiFields, over.id);
    if (multiNestedParent && multiNestedOverParent && multiNestedParent.parentId === multiNestedOverParent.parentId) {
      const oldIndex = multiNestedParent.items.findIndex((field) => field.id === active.id);
      const newIndex = multiNestedOverParent.items.findIndex((field) => field.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        setBackup({ fixed: fixedFields, multi: multiFields });
        setMultiFields((prev) =>
          reorderNestedFields(prev, multiNestedParent.parentId, oldIndex, newIndex)
        );
      }
    }
  };

  /* ---------------------------
     Expand/collapse, CRUD on fields
  ----------------------------*/
  const toggleExpand = (id) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleFixedLabelChange = (id, value, key = "labelText") => {
    setFixedFields((prev) =>
      updateFieldTree(prev, id, (field) => ({
        ...field,
        [key]: value,
        __errors: { ...field.__errors, [key]: null },
      }))
    );
  };

  const handleMultiLabelChange = (id, value, key = "labelText") => {
    setMultiFields((prev) =>
      updateFieldTree(prev, id, (field) => ({
        ...field,
        [key]: value,
        __errors: { ...field.__errors, [key]: null },
      }))
    );
  };

  const handleFixedDelete = (id) => {
    setBackup({ fixed: fixedFields, multi: multiFields });
    setFixedFields((prev) => deleteFieldFromTree(prev, id));
    setExpandedSet((s) => {
      const next = new Set(s);
      next.delete(id);
      return next;
    });
  };

  const handleMultiDelete = (id) => {
    setBackup({ fixed: fixedFields, multi: multiFields });
    setMultiFields((prev) => deleteFieldFromTree(prev, id));
    setExpandedSet((s) => {
      const next = new Set(s);
      next.delete(id);
      return next;
    });
  };

  const handleToggleChangeFixed = (id, checked) => {
    setFixedFields((p) =>
      p.map((i) => {
        if (i.id !== id) return i;
        if (checked) {
          const nestedFields = [{ label: i.labelText || i.label, type: i.originalType || i.type, placeholder: i.placeholder || "" }];
          return { ...i, toggle: true, nested: true, nestedFields, type: "repeater" };
        } else {
          return { ...i, toggle: false, nested: false, nestedFields: [], type: i.originalType || i.type };
        }
      })
    );
  };

  const handleToggleChangeMulti = (id, checked) => {
    setMultiFields((p) =>
      p.map((i) => {
        if (i.id !== id) return i;
        if (checked) {
          const nestedFields = [{ label: i.labelText || i.label, type: i.originalType || i.type, placeholder: i.placeholder || "" }];
          return { ...i, toggle: true, nested: true, nestedFields, type: "repeater" };
        } else {
          return { ...i, toggle: false, nested: false, nestedFields: [], type: i.originalType || i.type };
        }
      })
    );
  };

  const handleUndo = () => {
    setFixedFields(backup.fixed || []);
    setMultiFields(backup.multi || []);
    setExpandedSet(new Set());
  };

  /* ---------------------------
     Validation & Update
  ----------------------------*/
  const validateAll = () => {
    let ok = true;
    const newErrors = { templateName: null };

    if (!templateName || !templateName.trim()) {
      newErrors.templateName = "Template Name is required";
      ok = false;
    }

    const markInvalid = () => {
      ok = false;
    };

    const fixed = validateFieldTree(fixedFields, markInvalid);
    const multi = validateFieldTree(multiFields, markInvalid);

    setFixedFields(fixed);
    setMultiFields(multi);
    setErrors(newErrors);
    return ok;
  };

  const buildFinalPayload = (templateName, fixedFields, multiFields) => {
    const fixedPayload = fixedFields.map(serializeField);

    const multiPayloadFields = multiFields.map((m) => {
      if (m.toggle && m.nested) {
        // nested repeater logic
        const inner = (m.nestedFields && m.nestedFields[0]) || {};
        return {
          label: (m.labelText || m.label).trim(),
          type: "repeater",
          fields: [
            {
              label: (inner.labelText || inner.label || m.label).trim(),
              type: inner.type || m.originalType || "text",
              required: !!inner.required,
              placeholder: inner.placeholder || (m.placeholderEnabled ? (m.placeholder || "") : ""),
              standard_field:
                typeof inner.standard_field === "string"
                  ? inner.standard_field
                  : inner.standard_field?._id ||
                  (typeof m.standard_field === "string"
                    ? m.standard_field
                    : m.standard_field?._id) ||
                  null,

            },
          ],
        };
      }

      return serializeField(m);
    });

    const payload = {
      label: templateName,
      type: templateName.trim().toLowerCase().replace(/\s+/g, "-"),
      fields: [...fixedPayload],
    };

    if (multiPayloadFields.length > 0) {
      payload.fields.push({
        label: "Cards",
        type: "repeater",
        fields: multiPayloadFields,
      });
    }

    return payload;
  };

  const handleUpdate = async () => {
    setApiError("");
    if (!validateAll()) {
      const toOpen = new Set();
      collectMissingLabelIds(fixedFields, toOpen);
      collectMissingLabelIds(multiFields, toOpen);
      setExpandedSet((prev) => new Set([...Array.from(prev), ...Array.from(toOpen)]));
      return;
    }

    const payload = buildFinalPayload(templateName, fixedFields, multiFields);
    setLoading(true);
    try {
      const res = await UpdateTemplate(id, payload);
      if (res?.status === 1 && res.data) {
        // store local map for icons/labels restoration later (index-based)
        const fixedMap = fixedFields.map((f) => f.sourceLibId || null);
        const multiMap = multiFields.map((m) => m.sourceLibId || null);
        const mapObj = { fixed: fixedMap, multi: multiMap };
        localStorage.setItem(tplMapKey(res.data._id || id), JSON.stringify(mapObj));

        showToast("Template updated successfully", "success");
        navigate("/section");
        return;
      }
      setApiError(res?.message || "Failed to update template");
      showToast(res?.message || "Failed to update template", "error");
    } catch (err) {
      setApiError(err?.message || "Failed to update template");
      showToast(err?.message || "Failed to update template", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------
     Render
  ----------------------------*/
  return (
    <div>
      <PageHeader pageTitle="Template Detail">
        <button className="btn-white me-2" onClick={() => navigate("/section")}>
          Discard
        </button>
        <button className="btn-red me-2" onClick={() => setShowDeletePopup(true)}>
          Delete
        </button>
        <button className="btn-black" onClick={handleUpdate} disabled={loading}>
          {loading ? "Updating..." : "Update"}
        </button>

      </PageHeader>

      <div className="page-container">
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="row">
            <div className="col-md-5">
              <div className="theme-card mb-3 p-3">
                <div className="card-title mb-3">Template Details</div>
                <div className="frm-group">
                  <label className="frm-label d-flex align-items-center">Template Name
                    <img
                      className="ms-2"
                      src={infoCircle}
                      alt="Info"
                      title="Template Name"
                    />
                  </label>
                  <input type="text" placeholder="Enter Template Name" className="page-menu-item w-100" value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
                  {errors.templateName && <div className="input-error">{errors.templateName}</div>}
                  {apiError && <div className="input-error mt-2">{apiError}</div>}
                </div>
              </div>

              <div className="theme-card p-3">
                <div className="card-title mb-3">Standard Fields</div>
                {libLoading ? (
                  <div className="center-div">
                    <Spinner size="md" />
                  </div>
                ) : libError ? (
                  <div className="input-error">Failed to load fields</div>
                ) : (
                  <div className="draggable-fields">
                    {library.map((f) => (
                      <DraggableField key={f.id} id={f.id} label={f.label} icon={f.icon} payloadType={f.type} placeholderEnabled={f.placeholderEnabled} />
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-7">

              {/* SHOW LOADER FIRST */}
              {isFullLoading ? (
                <div className="theme-card p-5 text-center">
                  <div className="center-div">
                    <Spinner size="md" />
                  </div>
                </div>
              ) : (
                <div className="theme-card p-4">

                  <div className="card-title mb-4 d-flex justify-content-between align-items-center">
                    <div>Section Builder</div>

                    {(fixedFields.length > 0 || multiFields.length > 0) && (
                      <button className="undo-btn" onClick={handleUndo}>
                        <img src={undoIcon} alt="undo" /> Undo Changes
                      </button>
                    )}
                  </div>

                  {/* FIXED FIELDS */}
                  <DroppableFixed
                    fields={fixedFields}
                    onLabelChange={handleFixedLabelChange}
                    onDelete={handleFixedDelete}
                    expandedSet={expandedSet}
                    toggleExpand={toggleExpand}
                    onToggleChange={handleToggleChangeFixed}
                    previewIndex={dragOverInfo.containerId === "fixed-area" ? dragOverInfo.index : null}
                    showPreview={dragOverInfo.containerId === "fixed-area"}
                  />
                  {/* <div className="mb-3" ref={setFixedNodeRef}>
                    <h6>Fixed Fields</h6>

                    {fixedFields.length === 0 ? (
                      <EmptyBox
                        title="Drop Fixed Fields Here"
                        desc="Add single-instance fields like title or subtitle that appear once per section."
                      />
                    ) : (
                      <SortableContext
                        items={fixedFields.map((f) => f.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {fixedFields.map((it) => (
                          <SortableField
                            key={it.id}
                            item={it}
                            onLabelChange={handleFixedLabelChange}
                            onDelete={handleFixedDelete}
                            isExpanded={expandedSet.has(it.id)}
                            toggleExpand={toggleExpand}
                            onToggleChange={handleToggleChangeFixed}
                          />
                        ))}
                      </SortableContext>
                    )}
                  </div> */}

                  {/* MULTI FIELDS */}
                  <DroppableMulti
                    fields={multiFields}
                    onLabelChange={handleMultiLabelChange}
                    onDelete={handleMultiDelete}
                    expandedSet={expandedSet}
                    toggleExpand={toggleExpand}
                    onToggleChange={handleToggleChangeMulti}
                    previewIndex={dragOverInfo.containerId === "multi-area" ? dragOverInfo.index : null}
                    showPreview={dragOverInfo.containerId === "multi-area"}
                  />
                  {/* <div ref={setMultiNodeRef} className="drop-box">
                    <h6>Fields for Multiple Instance</h6>

                    {multiFields.length === 0 ? (
                      <EmptyBox
                        title="Drop Fields for Multiple Entries"
                        desc="Add input fields that generate repeatable sets such as testimonials or FAQs."
                      />
                    ) : (
                      <SortableContext
                        items={multiFields.map((f) => f.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {multiFields.map((it) => (
                          <SortableField
                            key={it.id}
                            item={it}
                            onLabelChange={handleMultiLabelChange}
                            onDelete={handleMultiDelete}
                            isExpanded={expandedSet.has(it.id)}
                            toggleExpand={toggleExpand}
                            onToggleChange={handleToggleChangeMulti}
                            isMulti={true}
                          />
                        ))}
                      </SortableContext>
                    )}
                  </div> */}

                </div>
              )}

            </div>

          </div>
          <DragOverlay>
            {activeId && activeItem ? (
              <FieldItem
                item={activeItem}
                isOverlay
                isMulti={activeItem.id?.includes('multi')}
                dragHandleProps={{}} // Already handled by wrapper usually, but here we just need visuals
                style={{}}
                expandedSet={expandedSet}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
      {showDeletePopup && (
        <div className="popup-overlay">
          <div className="popup-container">
            <h4 className="popup-title">Delete Template?</h4>
            <p className="popup-message">
              Are you sure you want to delete this template? This action cannot be undone.
            </p>

            <div className="popup-actions">
              <button
                className="btn-white"
                onClick={() => setShowDeletePopup(false)}
                disabled={deleteLoading}
              >
                No
              </button>

              <button
                className="btn-red"
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
